package top.wcpe.allincore.ui.runtime.element

import kotlin.jvm.JvmStatic

/**
 * 实体预览元素：用于在 GUI 中展示 3D 实体（玩家自身 / 其他玩家 / Mob 实体），vanilla 行为朝向跟随鼠标。
 * 具体 3D 渲染由各平台 RenderBridge 实现，ui-runtime 层仅维护数据绑定。
 *
 * source 字段：`"self"`（当前玩家自身）/ `"uuid:<uuid>"`（指定玩家）/ `"entity:<type>"`（指定 Mob 实体）。
 * visible=false 时元素不渲染但仍占据布局空间（与 Image 一致）。
 *
 * [scale] / [rotationX] / [rotationY] 是 3D 模型自身的缩放与朝向（注入实体渲染 PoseStack），
 * 与 TransformPropContributor 的 UI 通用 2D/透视变换（scaleX/scaleY/rotation/rotateX/rotateY）含义不同、互不影响。
 */
class EntityPreview private constructor() : UIElement<EntityPreview>() {

    private var sourceValue: String? = "self"
    private var followMouseFlag: Boolean = true
    private var entityTypeValue: String? = null
    private var entityNameValue: String? = null
    private var scaleValue: Float = DEFAULT_SCALE
    private var rotationXValue: Float = 0f
    private var rotationYValue: Float = 0f

    // === 视觉覆盖字段（可空，null = 不绘制 / 回退） ===
    private var backgroundColorValue: Int? = null
    private var hoverOverlayColorValue: Int? = null

    protected override fun self(): EntityPreview = this

    fun source(source: String?): EntityPreview {
        sourceValue = source
        return this
    }

    fun source(): String? = sourceValue

    fun followMouse(followMouse: Boolean): EntityPreview {
        followMouseFlag = followMouse
        return this
    }

    fun followMouse(): Boolean = followMouseFlag

    /** 设置实体类型 ID（namespaced）。null 表示未指定，渲染层将回退到 source。 */
    fun entityType(entityType: String?): EntityPreview {
        entityTypeValue = entityType
        return this
    }

    fun entityType(): String? = entityTypeValue

    /** 设置实体显示名（平台层写为虚拟实体 customName，供实体档案按名称匹配 / 读取）。 */
    fun entityName(entityName: String?): EntityPreview {
        entityNameValue = entityName
        return this
    }

    fun entityName(): String? = entityNameValue

    /** 设置模型缩放倍率。非正数兜底为 1f，避免 NaN。 */
    fun scale(scale: Float): EntityPreview {
        scaleValue = if (scale <= 0f) DEFAULT_SCALE else scale
        return this
    }

    fun scale(): Float = scaleValue

    /** 设置绕 X 轴旋转角度（度），原样存储不做 clamp。 */
    fun rotationX(rotationX: Float): EntityPreview {
        rotationXValue = rotationX
        return this
    }

    fun rotationX(): Float = rotationXValue

    /** 设置绕 Y 轴旋转角度（度），原样存储不做 clamp。 */
    fun rotationY(rotationY: Float): EntityPreview {
        rotationYValue = rotationY
        return this
    }

    fun rotationY(): Float = rotationYValue

    // === 视觉覆盖 fluent setter / getter ===

    /** 背景填充色覆盖；null 时不绘制背景。 */
    fun backgroundColor(v: Int?): EntityPreview {
        backgroundColorValue = v
        return this
    }

    fun backgroundColor(): Int? = backgroundColorValue

    /** hover 高亮叠加色覆盖；null 时不叠加。 */
    fun hoverOverlayColor(v: Int?): EntityPreview {
        hoverOverlayColorValue = v
        return this
    }

    fun hoverOverlayColor(): Int? = hoverOverlayColorValue

    companion object {
        /** 模型缩放默认倍率（1=原始大小）。 */
        private const val DEFAULT_SCALE = 1f

        /** 创建一个实体预览元素。 */
        @JvmStatic
        fun create(): EntityPreview = EntityPreview()
    }
}
